import React, { Dispatch, FC, SetStateAction, useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import {
  DealerApplication,
  DealerArea,
  DealerFullModel,
  UpdateDealerApplicationPayload,
} from '../../../../shared/dealer';
import { OptionDataPayload, OptionDataValue } from '../../../../shared/options';
import { ProductModel } from '../../../../shared/orders';
import { MessageCard, ProductOptionViewer } from '../../../components';
import { useTranslation } from '../../../translations';
import {
  displayName,
  Fetcher,
  handleUnknownOptionError,
  isLogicError,
  isNonEmpty,
  NonEmptyList,
  toggleElementInArray,
  useConfig,
  useFetcher,
  useForm,
  useInputModel,
  useObject,
  useUserSafe,
} from '../../../utils';
import { captureError, LogicError } from '../../../utils/errorHandling';
import { getAddonPrice } from '../../../utils/product';

interface DealerApplicationFormProps {
  readonly dealer: DealerFullModel;
  readonly dealerAreaId: number;
  readonly callback: () => void;
}

export function cleanupApplicationOptions(
  payload: OptionDataPayload,
  product: ProductModel,
): OptionDataPayload {
  const result: OptionDataPayload = {};

  for (const option of product.options) {
    const plVal = payload[option.id];
    if (plVal) {
      result[option.id] = plVal;
    }
  }

  for (const option of product.addons.flatMap((t) => t.options)) {
    const plVal = payload[option.id];
    if (plVal) {
      result[option.id] = plVal;
    }
  }

  return result;
}

export const SpaceApplicationForm: FC<DealerApplicationFormProps> = ({
  dealer,
  dealerAreaId,
  callback,
}) => {
  const config = useConfig();
  const { ts } = useTranslation();
  const area = config.dealersAreas.find((t) => t.id === dealerAreaId)!;
  const inputApplication = dealer.applications.find((t) => t.areaId === dealerAreaId);
  const tableTypesRequest = useFetcher(async () => {
    return await api.getProductsByCategory(area.categoryId, inputApplication?.tableType.id);
  }, [area.categoryId, inputApplication?.tableType.id]);

  if (!tableTypesRequest.data) {
    return <Fetcher result={tableTypesRequest} />;
  }

  if (!isNonEmpty(tableTypesRequest.data)) {
    return <Alert color="danger">{ts('no_table_types_available')}</Alert>;
  }

  return (
    <SpaceApplicationFormInner
      area={area}
      callback={callback}
      dealer={dealer}
      dealerAreaId={dealerAreaId}
      tableTypes={tableTypesRequest.data}
    />
  );
};

interface DealerApplicationFormPropsInner {
  readonly area: DealerArea;
  readonly dealer: DealerFullModel;
  readonly dealerAreaId: number;
  readonly tableTypes: NonEmptyList<ProductModel>;
  readonly callback: () => void;
}

const SpaceApplicationFormInner: FC<DealerApplicationFormPropsInner> = ({
  area,
  dealer,
  dealerAreaId,
  tableTypes,
  callback,
}) => {
  const { ts } = useTranslation();
  const inputApplication = dealer.applications.find((t) => t.areaId === dealerAreaId);
  const disabled = !!inputApplication && inputApplication.status !== 'pending';
  const [application, setApplication] = useState<UpdateDealerApplicationPayload>(() => ({
    requests: inputApplication?.dealerRequestTypes.map((t) => t.id) ?? [],
    specialRequests: inputApplication?.specialRequests || null,
    tableTypeId: inputApplication?.tableType.id ?? tableTypes[0].id,
    status: inputApplication?.status ?? 'pending',
    tableNumber: inputApplication?.tableNumber || null,
    options: inputApplication?.options ?? {},
  }));

  const form = useForm(async () => {
    const table = tableTypes.find((t) => t.id === application.tableTypeId)!;
    try {
      await api.updateDealerApplication(dealer.id, dealerAreaId, {
        ...application,
        options: cleanupApplicationOptions(application.options, table),
      });
    } catch (error) {
      if (isLogicError(error, LogicError.UnknownProductOption)) {
        handleUnknownOptionError(error.apiResponse.errors.logic, table.options);
      } else {
        throw error;
      }
    }

    callback();
    toast.success(ts('your_application_has_been_updated'));
  }, [tableTypes, callback, ts, application, dealer.id, dealerAreaId]);

  if (!area.open) {
    return (
      <Col xs={12}>
        <Alert color="danger">{ts('areaname_is_closed', area.name)}</Alert>
      </Col>
    );
  }

  // TODO(i18n): Update `application.status` to the correct language
  return (
    <form onSubmit={form.onSubmit}>
      <Row className="justify-content-center" id="areaApplicationForm">
        <Col lg={8} sm={12}>
          {area.full && (
            <Alert color="warning">{ts('the_areaname_is_full_applications', area.name)}</Alert>
          )}
          {disabled && (
            <Alert color="warning">
              {ts('the_application_is_applicationstatus_and', application.status)}
            </Alert>
          )}
        </Col>
        <Col className="mb-10" lg={8} sm={12}>
          <ApplicationBody
            application={application}
            disabled={disabled}
            isNew={!inputApplication}
            paidOrderItemId={inputApplication?.paidOrderItemId ?? undefined}
            setApplication={setApplication}
            tableTypes={tableTypes}
          />
        </Col>
        {tableTypes.length > 0 && (
          <Col className="mt-10" lg={6} sm={12}>
            <Button
              block
              color="primary"
              disabled={disabled || form.saving}
              id="submitAreaApplication"
              type="submit"
            >
              {ts(inputApplication ? 'common.update' : 'submit')}
            </Button>
          </Col>
        )}
        <Col sm={12} />
        <Col className="mt-10" lg={6} sm={12}>
          <DeleteApplicationButton
            application={inputApplication}
            dealerAreaId={dealerAreaId}
            dealerId={dealer.id}
          />
        </Col>
      </Row>
    </form>
  );
};

interface ApplicationBodyProps {
  readonly application: UpdateDealerApplicationPayload;
  readonly setApplication: Dispatch<SetStateAction<UpdateDealerApplicationPayload>>;
  readonly disabled: boolean;
  readonly tableTypes: ProductModel[];
  readonly paidOrderItemId: number | undefined;
  readonly isNew: boolean;
}

const ApplicationBody: FC<ApplicationBodyProps> = ({
  application,
  disabled,
  tableTypes,
  paidOrderItemId,
  setApplication,
  isNew,
}) => {
  const { ts } = useTranslation();
  if (tableTypes.length === 0) {
    return (
      <Col lg={12} xs={12}>
        <MessageCard icon="block" id="noTablesFailed" level="danger">
          <CardTitle>{ts('no_table_types_available')}</CardTitle>
          {ts('no_table_types_available_to')}
        </MessageCard>
      </Col>
    );
  }

  if (isNew) {
    return (
      <ApplicationCard
        application={application}
        disabled={disabled}
        setApplication={setApplication}
        tableTypes={tableTypes}
      />
    );
  }

  return (
    <Row className="justify-content-center">
      <Col lg={6} sm={12}>
        <ApplicationCard
          application={application}
          disabled={disabled}
          setApplication={setApplication}
          tableTypes={tableTypes}
        />
      </Col>
      <Col lg={6} sm={12}>
        <Card id="nextStepsCard">
          <CardBody>
            <CardTitle>{ts('next_steps')}</CardTitle>
            <NextStepsCard application={application} paidOrderItemId={paidOrderItemId} />
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

interface NextStepsCardProps {
  readonly application: UpdateDealerApplicationPayload;
  readonly paidOrderItemId: number | undefined;
}

const NextStepsCard: FC<NextStepsCardProps> = ({ application, paidOrderItemId }) => {
  const config = useConfig();
  const { t, ts } = useTranslation();
  if (application.status === 'pending') {
    return (
      <ul>
        <li>{ts('your_application_has_been_submitted')}</li>
      </ul>
    );
  }

  if (application.status === 'rejected') {
    return (
      <ul>
        <li>{ts('thank_you_for_taking_the')}</li>
      </ul>
    );
  }

  if (application.status === 'waitlisted') {
    return (
      <ul>
        <li>{ts('your_application_is_waitlisted_and')}</li>
        <li>{ts('you_will_be_contacted_if')}</li>
      </ul>
    );
  }

  if (!paidOrderItemId) {
    // TODO: Add payment amount and link.
    return (
      <ul>
        <li>{ts('your_application_was_approved_and')}</li>
        <li>
          {t(
            'if_you_have_any_assistants',
            <Link to="/vendor/assistants">{ts('vendor_assistants')}</Link>,
          )}
        </li>
        <li>{ts('if_you_need_to_edit', config.contact.email.dealers)}</li>
      </ul>
    );
  }

  return (
    <ul>
      <li>{ts('your_application_was_approved_and')}</li>
      <li>
        {t(
          'if_you_have_any_assistants',
          <Link to="/vendor/assistants">{ts('vendor_assistants')}</Link>,
        )}
      </li>
      <li>{ts('if_you_need_to_edit', config.contact.email.dealers)}</li>
    </ul>
  );
};

interface ApplicationCardProps {
  readonly application: UpdateDealerApplicationPayload;
  readonly setApplication: Dispatch<SetStateAction<UpdateDealerApplicationPayload>>;
  readonly disabled: boolean;
  readonly tableTypes: ProductModel[];
}

const ApplicationCard: FC<ApplicationCardProps> = ({
  application,
  setApplication,
  disabled,
  tableTypes,
}) => {
  const { ts } = useTranslation();
  const setSpecialRequests = useInputModel(setApplication, 'specialRequests');
  const [tableTypeId, setTableTypeId] = useObject(application, setApplication, 'tableTypeId');
  const selectedTableType = tableTypes.find((t) => application.tableTypeId === t.id);

  return (
    <Card>
      <CardBody>
        <CardTitle>{ts('space_information')}</CardTitle>
        <Row>
          <Col xs={12}>
            <FormGroup>
              <Label for="specialRequests">{ts('special_requests')}</Label>
              <Input
                disabled={disabled}
                id="specialRequests"
                onChange={setSpecialRequests}
                type="textarea"
                value={application.specialRequests ?? ''}
              />
            </FormGroup>
            <TableTypeSelect
              disabled={disabled}
              selectedTableTypeId={tableTypeId}
              setSelectedTableTypeId={setTableTypeId}
              tableTypes={tableTypes}
            />
          </Col>
          {selectedTableType && (
            <>
              <Col xs={12}>
                <ApplicationAddons
                  application={application}
                  disabled={disabled}
                  selectedTableType={selectedTableType}
                  setApplication={setApplication}
                />
              </Col>
              <Col xs={12}>
                <ApplicationOptionListRender
                  application={application}
                  disabled={disabled}
                  product={selectedTableType}
                  setApplication={setApplication}
                />
              </Col>
            </>
          )}
        </Row>
      </CardBody>
    </Card>
  );
};

interface TableTypeSelectProps {
  readonly tableTypes: ProductModel[];
  readonly selectedTableTypeId: number;
  readonly setSelectedTableTypeId: (value: number) => void;
  readonly disabled: boolean;
}

const TableTypeSelect: FC<TableTypeSelectProps> = ({
  tableTypes,
  selectedTableTypeId,
  setSelectedTableTypeId,
  disabled,
}) => {
  const { currency, ts } = useTranslation();

  return (
    <FormGroup>
      <Label for="tableTypeId">{ts('dealer.table_type')}</Label>
      <Input
        disabled={disabled}
        id="tableTypeId"
        onChange={(e) => {
          setSelectedTableTypeId(Number.parseInt(e.target.value, 10));
        }}
        type="select"
        value={selectedTableTypeId.toString()}
      >
        {tableTypes.map((tableType) => {
          return (
            <option key={tableType.id} value={tableType.id}>
              {displayName(tableType)} ({currency(tableType.price)})
            </option>
          );
        })}
      </Input>
      <small>{tableTypes.find((t) => selectedTableTypeId === t.id)?.description ?? ''}</small>
    </FormGroup>
  );
};

interface ApplicationAddonsProps {
  readonly selectedTableType?: ProductModel;
  readonly application: UpdateDealerApplicationPayload;
  readonly setApplication: Dispatch<SetStateAction<UpdateDealerApplicationPayload>>;
  readonly disabled: boolean;
}

const ApplicationAddons: FC<ApplicationAddonsProps> = ({
  application,
  selectedTableType,
  setApplication,
  disabled,
}) => {
  if (!selectedTableType) {
    return null;
  }

  return selectedTableType.addons.map((addon) => {
    const key = `requests${addon.id}`;
    return (
      <FormGroup check className="mt-10" key={addon.id}>
        <Input
          checked={application.requests.includes(addon.id)}
          disabled={disabled}
          id={key}
          onChange={() => {
            setApplication((old) => ({
              ...old,
              requests: toggleElementInArray(old.requests, addon.id),
            }));
          }}
          type="checkbox"
          value={addon.id}
        />
        <Label check for={key}>
          {displayName(addon)} {!addon.isFree && <>({getAddonPrice(addon).price})</>}
        </Label>
      </FormGroup>
    );
  });
};

const DeleteApplicationButton: FC<{
  readonly dealerId: number;
  readonly dealerAreaId: number;
  readonly application?: DealerApplication;
}> = ({ dealerAreaId, application, dealerId }) => {
  const history = useHistory();
  const user = useUserSafe();
  const { ts } = useTranslation();

  if (!application || application.status !== 'pending') {
    return null;
  }

  async function deleteApplication(): Promise<void> {
    try {
      await api.deleteActiveDealerApplication(dealerId, dealerAreaId);
      let dealer = null;
      try {
        dealer = await api.getUserDealer(user.id);
      } catch {
        /* NO-OP */
      }

      toast.success(ts('your_vendor_application_has_been'));
      history.push(dealer ? '/vendor' : '/');
    } catch (error) {
      captureError(error as Error);
    }
  }

  return (
    <Button block color="danger" id="deleteApplication" onClick={deleteApplication}>
      {ts('common.delete_application')}
    </Button>
  );
};

interface ApplicationOptionListRenderProps {
  readonly application: UpdateDealerApplicationPayload;
  readonly setApplication: Dispatch<SetStateAction<UpdateDealerApplicationPayload>>;
  readonly product: ProductModel;
  readonly disabled: boolean;
}

export const ApplicationOptionListRender: FC<ApplicationOptionListRenderProps> = ({
  product,
  disabled,
  application,
  setApplication,
}) => {
  const onOptionChange = useCallback(
    (id: number, value: OptionDataValue) => {
      setApplication((old) => ({
        ...old,
        options: {
          ...old.options,
          [id]: value,
        },
      }));
    },
    [setApplication],
  );

  const options = [...product.options];

  for (const addon of product.addons) {
    if (addon.isFree || application.requests.includes(addon.id)) {
      options.push(...addon.options);
    }
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <div>
      <hr />
      {options.map((option) => {
        return (
          <ProductOptionViewer
            disabled={disabled}
            key={option.id}
            onChange={onOptionChange}
            option={option}
            value={application.options[option.id]}
          />
        );
      })}
    </div>
  );
};
